// Voir et corriger les catégories d'un profil ou d'une offre.
//
// C'EST LA MOITIÉ VISIBLE DU CHANGEMENT, et sans elle il n'a pas lieu. Le
// pré-filtre qu'on remplace décidait aussi bien ou aussi mal ; ce qu'on lui
// reprochait est qu'il décidait SANS QU'ON PUISSE LE VOIR NI LE CORRIGER. Un
// écran qui montrerait les catégories sans permettre de les changer
// reproduirait exactement le défaut, en plus bavard.
//
// D'où deux choses que ce composant fait et qui ne sont pas décoratives :
// il affiche l'ORIGINE de chaque catégorie (proposée ou corrigée), et il
// affiche le CHEMIN complet, parce qu'un code à quatre chiffres ne dit rien
// et que « Concepteurs de logiciels » seul ne dit pas dans quelle branche on
// se trouve.

import { useEffect, useState } from 'react'
import { Check, Pencil, Search, X } from 'lucide-react'
import { get, put, ApiError } from '../lib/api'
import { ActionButton, useToast } from './common'
import { useT } from '../lib/i18n'
import { signalerTache } from './bandeauTaches'

export interface CategorieAssignee {
  code: string
  origine: 'ia' | 'humain'
  label: string
  chemin: string[]
  voisins: { code: string; label: string }[]
}

/** Le chemin sans le dernier élément, qui est déjà le libellé affiché. */
const contexte = (chemin: string[]) => chemin.slice(0, -1).join(' › ')

export function Categories({
  base,
  editable = true,
  onChanged,
}: {
  /** `/api/consultants/12` ou `/api/offers/34`. */
  base: string
  editable?: boolean
  onChanged?: () => void
}) {
  const t = useT()
  const [assignees, setAssignees] = useState<CategorieAssignee[] | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)
  const [edition, setEdition] = useState(false)

  const recharger = () => {
    get<CategorieAssignee[]>(`${base}/categories`)
      .then((r) => { setAssignees(r); setErreur(null) })
      .catch((e) => setErreur(e instanceof ApiError ? e.message : t('categories.loadError')))
  }
  useEffect(recharger, [base])

  if (erreur) return <div className="cat-vide">{erreur}</div>
  if (!assignees) return null

  return (
    <div className="catbloc">
      <div className="pool-h">
        {t('categories.title')}
        <span className="grow" />
        {editable && (
          <button className="chip" onClick={() => setEdition(!edition)}>
            {edition ? <X size={13} /> : <Pencil size={13} />}
            {edition ? t('categories.close') : t('categories.edit')}
          </button>
        )}
      </div>

      {assignees.length === 0 ? (
        <div className="cat-vide">{t('categories.empty')}</div>
      ) : (
        <div className="catlist">
          {assignees.map((c) => (
            <div key={c.code} className="catitem">
              <div className="grow">
                <div className="cat-chemin">{contexte(c.chemin)}</div>
                <div className="cat-label">{c.label}</div>
              </div>
              {/* L'origine n'est pas un détail : elle dit si quelqu'un a déjà
                  tranché, et donc si un rattrapage automatique y touchera. */}
              <span className={`pill${c.origine === 'humain' ? ' placed' : ''}`}>
                {c.origine === 'humain' ? t('categories.originHuman') : t('categories.originAi')}
              </span>
            </div>
          ))}
        </div>
      )}

      {edition && (
        <EditeurCategories
          base={base}
          actuelles={assignees.map((a) => ({ code: a.code, label: a.label }))}
          onFini={() => { setEdition(false); recharger(); onChanged?.() }}
        />
      )}

      {!edition && assignees.some((a) => a.voisins.length > 0) && (
        <details className="cat-voisins">
          <summary>{t('categories.neighbours')}</summary>
          <p className="field-help">{t('categories.neighboursHelp')}</p>
          <div className="catchips">
            {[...new Map(assignees.flatMap((a) => a.voisins).map((v) => [v.code, v])).values()].map((v) => (
              <span key={v.code} className="chip">{v.label}</span>
            ))}
          </div>
        </details>
      )}
    </div>
  )
}

/** Un groupe retenu, avec le libellé qui s'affiche sur sa pastille. */
export interface Choix {
  code: string
  label: string
}

interface Suggestion {
  code: string
  label: string
  metier: string | null
}

/**
 * Le choix des métiers : UN champ, une liste qui s'ouvre sous lui.
 *
 * POURQUOI PLUS DE CASCADE. La version d'avant faisait choisir un grand
 * domaine, puis une famille, puis cocher dans une liste : trois gestes dans
 * trois cases pour un seul métier, et le vocabulaire de la classification à
 * connaître d'avance. Personne ne sait que « développeur » vit sous
 * « Professions intellectuelles et scientifiques ». On tape ce qu'on fait, la
 * liste montre les MÉTIERS qui répondent et le groupe qu'ils ouvrent ; seul le
 * groupe est enregistré, parce que c'est lui qui décide des offres évaluées.
 *
 * Contrôlé : le parent tient la liste et décide quand l'enregistrer (bouton sur
 * la fiche, « Suivant » dans la prise en main).
 */
export function ChoixMetiers({ choix, onChange }: { choix: Choix[]; onChange: (c: Choix[]) => void }) {
  const t = useT()
  const [q, setQ] = useState('')
  const [resultats, setResultats] = useState<Suggestion[] | null>(null)
  const [actif, setActif] = useState(0)
  const [ouvert, setOuvert] = useState(false)

  useEffect(() => {
    if (q.trim().length < 2) { setResultats(null); return }
    let vivant = true
    const id = setTimeout(() => {
      get<Suggestion[]>(`/api/categories/recherche?q=${encodeURIComponent(q)}`)
        .then((r) => { if (vivant) { setResultats(r); setActif(0) } })
        .catch(() => { if (vivant) setResultats([]) })
    }, 200)
    return () => { vivant = false; clearTimeout(id) }
  }, [q])

  const codes = new Set(choix.map((c) => c.code))
  const retirer = (code: string) => onChange(choix.filter((c) => c.code !== code))
  const prendre = (s: Suggestion) => {
    if (!codes.has(s.code)) onChange([...choix, { code: s.code, label: s.label }])
    setQ('')
    setResultats(null)
  }

  const clavier = (e: React.KeyboardEvent) => {
    const n = resultats?.length ?? 0
    if (e.key === 'ArrowDown' && n > 0) { e.preventDefault(); setOuvert(true); setActif((a) => (a + 1) % n) }
    else if (e.key === 'ArrowUp' && n > 0) { e.preventDefault(); setActif((a) => (a - 1 + n) % n) }
    else if (e.key === 'Enter' && resultats?.[actif]) { e.preventDefault(); prendre(resultats[actif]) }
    // Échap referme la LISTE, pas la modale qui l'entoure : sans l'arrêt de la
    // propagation, un geste pour fermer une suggestion jetait toute la prise en
    // main. Liste fermée, Échap redevient celui de la modale.
    else if (e.key === 'Escape' && ouvert && resultats !== null) { e.stopPropagation(); setOuvert(false) }
  }

  return (
    <div className="choixmetiers">
      {choix.length === 0 ? (
        <div className="field-help">{t('categories.selectedEmpty')}</div>
      ) : (
        <div className="catchips">
          {choix.map((c) => (
            <button key={c.code} className="chip chip-danger" onClick={() => retirer(c.code)} title={t('categories.remove')}>
              {c.label}<X size={12} />
            </button>
          ))}
        </div>
      )}

      <div className="combo">
        <div className="search">
          <Search size={14} />
          <input
            role="combobox"
            aria-expanded={ouvert && resultats !== null}
            aria-label={t('categories.searchLabel')}
            placeholder={t('categories.searchPlaceholder')}
            value={q}
            onChange={(e) => { setQ(e.target.value); setOuvert(true) }}
            onFocus={() => setOuvert(true)}
            onBlur={() => setOuvert(false)}
            onKeyDown={clavier}
          />
        </div>
        {ouvert && resultats !== null && (
          <div className="combo-liste" role="listbox">
            {resultats.length === 0 && <div className="combo-vide">{t('categories.noResult')}</div>}
            {resultats.map((r, i) => (
              <div
                key={`${r.code}|${r.metier}`}
                role="option"
                aria-selected={i === actif}
                className={`combo-item${i === actif ? ' actif' : ''}`}
                // `mousedown` et pas `click` : le clic arrive APRÈS la perte de
                // focus du champ, qui a déjà refermé la liste.
                onMouseDown={(e) => { e.preventDefault(); prendre(r) }}
                onMouseEnter={() => setActif(i)}
              >
                <span className="grow">
                  <span className="combo-metier">{majuscule(r.metier ?? r.label)}</span>
                  {r.metier && <span className="combo-groupe">{r.label}</span>}
                </span>
                {codes.has(r.code) && <Check size={14} />}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

const majuscule = (s: string) => s.charAt(0).toUpperCase() + s.slice(1)

/** L'édition sur une fiche : le champ, et un bouton pour enregistrer. */
function EditeurCategories({
  base,
  actuelles,
  onFini,
}: {
  base: string
  actuelles: Choix[]
  onFini: () => void
}) {
  const t = useT()
  const toast = useToast()
  const [choix, setChoix] = useState<Choix[]>(actuelles)

  return (
    <div className="cateditor">
      <ChoixMetiers choix={choix} onChange={setChoix} />
      <div className="ondemand-row">
        <span className="grow" />
        <ActionButton
          busyLabel={t('categories.saving')}
          onClick={async () => {
            try {
              await put(`${base}/categories`, { codes: choix.map((c) => c.code) })
              toast(t('categories.savedToast'))
              signalerTache()
              onFini()
            } catch (e: any) { toast(e.message, 'err') }
          }}
        >
          <Check size={15} />{t('categories.save')}
        </ActionButton>
      </div>
    </div>
  )
}
